import React, { useEffect, useState, useRef, useContext } from 'react';
import { ShoppingBag, Menu, X, Search } from 'lucide-react';
import { NavigationContext, CartContext, Page } from '../App';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/rrMcdWMWNCqHewjNXCcsn6/tmp_eb6d3570-1adb-41f6-bf95-edf1a21eebde.png';
// Product images from Christiana's collection
const IMAGES = {
  blackPeplum: 'https://cdn.magicpatterns.com/uploads/k3nbovxYYDH3962AQgD28u/photo_2026-02-04_04-57-03.jpg',
  brownPleated: 'https://cdn.magicpatterns.com/uploads/dyohvQLDScUhskTJ884wYi/photo_2026-02-04_04-57-01.jpg',
  purpleFringe: 'https://cdn.magicpatterns.com/uploads/rrAJzJnT1RaD9iNVi3fG4e/photo_2026-02-04_04-57-00.jpg',
  tealSuit: 'https://cdn.magicpatterns.com/uploads/3hrnMEGcTHukVuERpb86Hi/photo_2026-02-04_04-57-05.jpg',
  greenMaxi: 'https://cdn.magicpatterns.com/uploads/n3jybvjJ42ADZp6V5BmhiN/photo_2026-02-04_04-57-04.jpg',
  goldPleated: 'https://cdn.magicpatterns.com/uploads/ksWTzFirUa358KYJesShzR/photo_2026-02-04_04-56-59.jpg',
  blackEmbroidered: 'https://cdn.magicpatterns.com/uploads/8Pmu6fAwDDrQ92G1L4HjJg/photo_2026-02-04_04-56-58.jpg',
  emeraldSuit: 'https://cdn.magicpatterns.com/uploads/wYFN1VGG9zCp3b6QMLCMkr/photo_2026-02-04_04-56-56.jpg'
};
const searchableProducts = [{
  id: 1,
  name: 'Black Peplum & Sequin Skirt Set',
  category: 'Dresses',
  price: '$185.00',
  image: IMAGES.blackPeplum
}, {
  id: 2,
  name: 'Brown Pleated Midi Dress',
  category: 'Dresses',
  price: '$145.00',
  image: IMAGES.brownPleated
}, {
  id: 3,
  name: 'Purple Fringe Embellished Dress',
  category: 'Dresses',
  price: '$210.00',
  image: IMAGES.purpleFringe
}, {
  id: 4,
  name: 'Teal Crystal Embellished Suit',
  category: 'Tops',
  price: '$195.00',
  image: IMAGES.tealSuit
}, {
  id: 5,
  name: 'Green Embroidered Maxi Dress',
  category: 'Dresses',
  price: '$175.00',
  image: IMAGES.greenMaxi
}, {
  id: 6,
  name: 'Gold Puff Sleeve Pleated Gown',
  category: 'Dresses',
  price: '$225.00',
  image: IMAGES.goldPleated
}, {
  id: 7,
  name: 'Black & Gold Embroidered Kaftan',
  category: 'Dresses',
  price: '$245.00',
  image: IMAGES.blackEmbroidered
}, {
  id: 8,
  name: 'Emerald Crystal Belted Suit',
  category: 'Tops',
  price: '$195.00',
  image: IMAGES.emeraldSuit
}];
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const {
    navigate
  } = useContext(NavigationContext);
  const {
    cartCount
  } = useContext(CartContext);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [isSearchOpen]);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
        setSearchQuery('');
      }
    };
    if (isSearchOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isSearchOpen]);
  const handleNavClick = (page: Page) => {
    navigate(page);
    setIsMobileMenuOpen(false);
  };
  const handleSearchClick = () => {
    setIsSearchOpen(true);
    setIsMobileMenuOpen(false);
  };
  const handleProductClick = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
    navigate('product');
  };
  const handleViewAllResults = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
    navigate('shop');
  };
  const filteredProducts = searchQuery.trim() ? searchableProducts.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.category.toLowerCase().includes(searchQuery.toLowerCase())) : [];
  return <>
      <nav className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'bg-[#FDF8F3]/95 backdrop-blur-sm shadow-sm py-1' : 'bg-[#FDF8F3] py-2'}`} data-id="element-271">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-272">
          <div className="flex justify-between items-center" data-id="element-273">
            {/* Logo - Left on mobile, stays left on desktop */}
            <div className="flex-shrink-0 flex items-center" data-id="element-274">
              <button onClick={() => handleNavClick('home')} className="block" data-id="element-275">
                <img src={LOGO_URL} alt="Christy Fashion Collection" className="h-[62px] sm:h-[66px] md:h-[74px] w-auto object-contain" data-id="element-276" />
              </button>
            </div>

            {/* Desktop navigation links */}
            <div className="hidden md:flex items-center space-x-8" data-id="element-277">
              <button onClick={() => handleNavClick('shop')} className="text-base font-medium text-[#5C3D2E] hover:text-[#D4A853] transition-colors uppercase tracking-widest" data-id="element-278">
                Shop
              </button>
              <button onClick={() => handleNavClick('collections')} className="text-base font-medium text-[#5C3D2E] hover:text-[#D4A853] transition-colors uppercase tracking-widest" data-id="element-279">
                Collections
              </button>
              <button onClick={() => handleNavClick('about')} className="text-base font-medium text-[#5C3D2E] hover:text-[#D4A853] transition-colors uppercase tracking-widest" data-id="element-280">
                Our Story
              </button>
              <button onClick={() => handleNavClick('contact')} className="text-base font-medium text-[#5C3D2E] hover:text-[#D4A853] transition-colors uppercase tracking-widest" data-id="element-281">
                Contact
              </button>
            </div>

            {/* Right icons - Mobile: Search, Cart, Menu | Desktop: Search, Cart */}
            <div className="flex items-center" data-id="element-282">
              {/* Search - always visible */}
              <button onClick={handleSearchClick} className="p-2 text-[#5C3D2E] hover:text-[#D4A853] transition-colors" aria-label="Search" data-id="element-283">
                <Search size={22} strokeWidth={1.5} data-id="element-284" />
              </button>

              {/* Cart - always visible */}
              <button onClick={() => handleNavClick('cart')} className="p-2 text-[#5C3D2E] hover:text-[#D4A853] transition-colors relative" data-id="element-285">
                <ShoppingBag size={22} strokeWidth={1.5} data-id="element-286" />
                {cartCount > 0 && <span className="absolute top-0 right-0 h-5 w-5 bg-[#D4A853] rounded-full text-xs text-[#1A1A1A] font-semibold flex items-center justify-center" data-id="element-287">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>}
              </button>

              {/* Hamburger menu - mobile only, now on the right */}
              <button className="md:hidden p-2 text-[#5C3D2E] ml-1" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-288">
                {isMobileMenuOpen ? <X size={24} data-id="element-289" /> : <Menu size={24} data-id="element-290" />}
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-[#FDF8F3] border-t border-[#D4A853]/20 py-4 px-4 shadow-lg" data-id="element-291">
            <div className="flex flex-col space-y-4" data-id="element-292">
              <button onClick={() => handleNavClick('shop')} className="text-base font-medium text-[#5C3D2E] uppercase tracking-widest py-2 border-b border-[#D4A853]/20 text-left" data-id="element-293">
                Shop
              </button>
              <button onClick={() => handleNavClick('collections')} className="text-base font-medium text-[#5C3D2E] uppercase tracking-widest py-2 border-b border-[#D4A853]/20 text-left" data-id="element-294">
                Collections
              </button>
              <button onClick={() => handleNavClick('about')} className="text-base font-medium text-[#5C3D2E] uppercase tracking-widest py-2 border-b border-[#D4A853]/20 text-left" data-id="element-295">
                Our Story
              </button>
              <button onClick={() => handleNavClick('contact')} className="text-base font-medium text-[#5C3D2E] uppercase tracking-widest py-2 text-left" data-id="element-296">
                Contact
              </button>
            </div>
          </div>}
      </nav>

      {isSearchOpen && <div className="fixed inset-0 z-[100]" data-id="element-297">
          <div className="absolute inset-0 bg-[#5C3D2E]/60 backdrop-blur-sm" onClick={() => {
        setIsSearchOpen(false);
        setSearchQuery('');
      }} data-id="element-298" />
          <div className="absolute top-0 left-0 right-0 bg-[#FDF8F3] shadow-xl" data-id="element-299">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-id="element-300">
              <div className="relative" data-id="element-301">
                <Search size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B5B4F]" strokeWidth={1.5} data-id="element-302" />
                <input ref={searchInputRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search for products..." className="w-full pl-12 pr-12 py-4 bg-white border border-[#D4A853]/30 text-[#5C3D2E] text-lg font-['Raleway'] placeholder-[#6B5B4F] focus:outline-none focus:border-[#D4A853] transition-colors" data-id="element-303" />
                <button onClick={() => {
              setIsSearchOpen(false);
              setSearchQuery('');
            }} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B5B4F] hover:text-[#5C3D2E] transition-colors" aria-label="Close search" data-id="element-304">
                  <X size={22} strokeWidth={1.5} data-id="element-305" />
                </button>
              </div>

              {searchQuery.trim() && <div className="mt-4 max-h-[60vh] overflow-y-auto" data-id="element-306">
                  {filteredProducts.length > 0 ? <>
                      <p className="text-sm text-[#6B5B4F] font-['Raleway'] uppercase tracking-wider mb-3" data-id="element-307">
                        {filteredProducts.length} result
                        {filteredProducts.length !== 1 ? 's' : ''} found
                      </p>
                      <div className="space-y-2" data-id="element-308">
                        {filteredProducts.slice(0, 6).map(product => <button key={product.id} onClick={handleProductClick} className="w-full flex items-center gap-4 p-3 bg-white hover:bg-[#F5E6D3] border border-[#D4A853]/20 transition-colors text-left group" data-id="element-309">
                            <div className="w-16 h-16 flex-shrink-0 overflow-hidden bg-[#F5E6D3]" data-id="element-310">
                              <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" data-id="element-311" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-312">
                              <h4 className="text-base font-['Playfair_Display'] text-[#5C3D2E] group-hover:text-[#D4A853] transition-colors truncate" data-id="element-313">
                                {product.name}
                              </h4>
                              <p className="text-sm text-[#6B5B4F] font-['Raleway']" data-id="element-314">
                                {product.category}
                              </p>
                            </div>
                            <p className="text-base text-[#5C3D2E] font-['Raleway'] font-medium" data-id="element-315">
                              {product.price}
                            </p>
                          </button>)}
                      </div>
                      {filteredProducts.length > 6 && <button onClick={handleViewAllResults} className="w-full mt-4 py-3 text-base text-[#D4A853] hover:text-[#C65D3B] font-['Raleway'] font-medium uppercase tracking-wider transition-colors" data-id="element-316">
                          View all {filteredProducts.length} results
                        </button>}
                    </> : <div className="text-center py-8" data-id="element-317">
                      <p className="text-[#6B5B4F] font-['Raleway'] mb-2 text-lg" data-id="element-318">
                        No products found for "{searchQuery}"
                      </p>
                      <button onClick={handleViewAllResults} className="text-base text-[#D4A853] hover:text-[#C65D3B] font-['Raleway'] underline underline-offset-2 transition-colors" data-id="element-319">
                        Browse all products
                      </button>
                    </div>}
                </div>}

              {!searchQuery.trim() && <div className="mt-6" data-id="element-320">
                  <p className="text-sm text-[#6B5B4F] font-['Raleway'] uppercase tracking-wider mb-3" data-id="element-321">
                    Popular Searches
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-322">
                    {['Dresses', 'Tops', 'Two-Piece Sets', 'New Arrivals'].map(term => <button key={term} onClick={() => setSearchQuery(term)} className="px-4 py-2 text-base text-[#5C3D2E] bg-white border border-[#D4A853]/30 hover:border-[#D4A853] hover:text-[#D4A853] font-['Raleway'] transition-colors" data-id="element-323">
                          {term}
                        </button>)}
                  </div>
                </div>}
            </div>
          </div>
        </div>}
    </>;
}